import React, { useEffect, useRef, useState } from 'react';
import { X, Phone, Mic, Headphones, ArrowRightLeft, Play, Download, XCircle, Bot as BotIcon } from 'lucide-react';
import { CallLog, AudioSession, Recording, CallTransfer, DtmfEvent } from 'types';
import { sessionsService } from 'api/services/sessions.service';
import { recordingsService } from 'api/services/recordings.service';
import { formatCallDuration, formatDate, formatBytes, getStatusColor, maskSensitiveNumbers, parseTranscript, formatTimestamp } from 'utils/helpers';
import TranscriptViewer from 'components/Common/TranscriptViewer';
import CallTranscriptPanel from 'components/Calls/CallTranscriptPanel';
import { seekAudio } from 'utils/callTranscript';
import { LoadingSpinner } from 'components/Common/LoadingSpinner';
import AudioPlayer from 'components/Common/AudioPlayer';

interface CallDetailsModalProps {
  call: CallLog;
  onClose: () => void;
  organizationId?: string; // Organization ID for proper tenant routing
}

const CallDetailsModal: React.FC<CallDetailsModalProps> = ({ call, onClose, organizationId }) => {
  const [session, setSession] = useState<AudioSession | null>(null);
  // const [recording, setRecording] = useState<Recording | null>(null);
  const [loading, setLoading] = useState(true);
  const [showPlayer, setShowPlayer] = useState(false);
  // Pressing Play used to only REVEAL the player; the reader then had to press
  // play again on a different control to hear anything.
  const [pressedPlay, setPressedPlay] = useState(false);
  const [authenticatedUrl, setAuthenticatedUrl] = useState<string>('');
  // The open player's <audio>, so a transcript timestamp can seek it.
  const audioEl = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    const fetchDetails = async () => {
      setLoading(true);

      // Fetch related session - pass organizationId for proper access control
      const sessionResponse = await sessionsService.getAudioSessions({
        callLogId: call.id,
        organizationId: organizationId
      });

      if (sessionResponse.success && sessionResponse.data?.data && sessionResponse.data.data.length > 0) {
        setSession(sessionResponse.data.data[0]);

        // Fetch recording if session exists - pass organizationId
        // const recordingResponse = await recordingsService.getRecordings({
        //   sessionId: sessionResponse.data.data[0].id,
        //   organizationId: organizationId
        // });

        // if (recordingResponse.success && recordingResponse.data?.data && recordingResponse.data.data.length > 0) {
        //   setRecording(recordingResponse.data.data[0]);
        // }
      }
      
      setLoading(false);
    };

    fetchDetails();
  }, [call.id, organizationId]);
  const handleDownloadRecording = async () => {
    // Same ladder as the play button. It used to append the session token to
    // the stored URL, which on this estate names recordings.greengiraffe.in —
    // a host that wants the short-lived token cpass mints as `?t=` and reads
    // `?token=` as no token at all. Every download 401'd on calls that played
    // perfectly well.
    if (!call) return;

    try {
      const callId = (call as any)?.call_id || call.unique_id;
      const url = await recordingsService.resolveDownloadUrl(callId, call.recording_url, organizationId);
      if (!url) {
        setPlaybackError('No recording is held for this call.');
        return;
      }
      setPlaybackError(null);
      // .wav is what the switch writes and what the CDN serves; the old .mp3
      // name handed people a file their player refused to open.
      recordingsService.startDownload(url, `recording-${callId}.wav`);
    } catch (error) {
      setPlaybackError('The recording could not be downloaded.');
      console.error('Download failed:', error);
    }
  };


  const [playbackError, setPlaybackError] = useState<string | null>(null);

  const handlePlayRecording = async () => {
    setPressedPlay(true);
    if (!call) return;

    // Same ladder as the Audio Session modal: the server-signed copy first,
    // because its key is scoped to this caller's organisation, then the stored
    // URL with the session token appended for backends that hold no copy.
    try {
      const callId = (call as any)?.call_id || call.unique_id;
      const url = await recordingsService.resolvePlayableUrl(callId, call.recording_url, organizationId);
      if (!url) {
        // Previously this only ever reached console.error, so pressing play on
        // a call with no recording appeared to do nothing at all.
        setPlaybackError('No recording is held for this call.');
        return;
      }
      setPlaybackError(null);
      setAuthenticatedUrl(url);
      setShowPlayer(true);
    } catch (error) {
      setPlaybackError('The recording could not be opened.');
      console.error('Play failed:', error);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        <div className="p-6 border-b flex items-center justify-between flex-shrink-0">
          <h3 className="text-xl font-bold">Call Details: {call.unique_id}</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6 overflow-y-auto flex-1">
          {/* Call Information */}
          <div>
            <h4 className="font-semibold text-lg mb-3 flex items-center gap-2">
              <Phone className="w-5 h-5" />
              Call Information
            </h4>
            <div className="grid grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg">
              <div>
                <p className="text-sm text-gray-600">Caller ID</p>
                <p className="font-medium">{call.caller_id}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Dialed Number</p>
                <p className="font-medium">{call.dialed_number}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Call Type</p>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  call.call_type === 'IN' 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-purple-100 text-purple-800'
                }`}>
                  {call.call_type}
                </span>
              </div>
              <div>
                <p className="text-sm text-gray-600">Status</p>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  getStatusColor(call.call_status)
                }`}>
                  {call.call_status}
                </span>
              </div>
              <div>
                <p className="text-sm text-gray-600">Duration</p>
                <p className="font-medium">{formatCallDuration(call.duration)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Start Time</p>
                <p className="font-medium">{formatDate(call.start_time)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Business ID</p>
                <p className="font-medium">{call.business_id}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Agent ID</p>
                <p className="font-medium">{call.agent_id}</p>
              </div>
            </div>
          </div>

          {loading ? (
            <LoadingSpinner />
          ) : (
            <>
              {/* Audio Session */}
              {session && (
                <div>
                  <h4 className="font-semibold text-lg mb-3 flex items-center gap-2">
                    <Mic className="w-5 h-5" />
                    Audio Session
                  </h4>
                  <div className="bg-blue-50 rounded-lg p-4">
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <p className="text-sm text-gray-600">Session Ref</p>
                        <p className="font-medium">{session.session_ref}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Audio Duration</p>
                        <p className="font-medium">{(session.audio_duration_ms / 1000).toFixed(1)}s</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Transcription Events</p>
                        <p className="font-medium">{session.transcription_events}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">DTMF Events</p>
                        <p className="font-medium">{session.dtmf_events}</p>
                      </div>
                    </div>
                    {session.call_summary && (
                      <div className="mt-4">
                        <p className="text-sm font-medium text-gray-700 mb-2">Call Summary</p>
                        <p className="text-sm bg-white p-3 rounded border">{maskSensitiveNumbers(session.call_summary)}</p>
                      </div>
                    )}
                    {session.transfer_call_summary && (
                      <div className="mt-4">
                        <p className="text-sm font-medium text-orange-700 mb-2 flex items-center gap-2">
                          <ArrowRightLeft className="w-4 h-4" />
                          Transfer Call Summary
                        </p>
                        <p className="text-sm bg-orange-50 p-3 rounded border border-orange-200">{maskSensitiveNumbers(session.transfer_call_summary)}</p>
                      </div>
                    )}
                    {/* AI Conversation Transcript */}
                    {session.transcript && <TranscriptViewer transcriptJson={session.transcript} />}

                    {/* Legacy Transcription (fallback) */}
                    {session.transcription && !session.transcript && (
                      <div className="mt-4">
                        <p className="text-sm font-medium text-gray-700 mb-2">Transcription</p>
                        <div className="text-sm bg-white p-3 rounded border whitespace-pre-wrap font-mono text-xs">
                          {session.transcription}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Recording
                *
                * Gated on having a call IDENTIFIER, not on recording_url.
                *
                * recording_url is the URL the switch wrote, and on these
                * deployments it is usually EMPTY — cpass locates the audio from
                * the call id by asking the recordings CDN, which is the whole
                * point of the resolver. Gating the section on recording_url
                * therefore hid the player on exactly the calls the resolver can
                * play, which is every call on the tvoice portals.
                *
                * The same fix was already made in AudioSessionModal; this copy
                * was missed. */}
              {(call.recording_url || (call as any)?.call_id || call.unique_id) && (
                <div>
                  <h4 className="font-semibold text-lg mb-3 flex items-center gap-2">
                    <Headphones className="w-5 h-5" />
                    Call Recording
                  </h4>
                  <div className="bg-green-50 rounded-lg p-4">
                    {!showPlayer && (
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Headphones className="w-8 h-8 text-green-600" />
                          <div>
                            <p className="font-medium">Recording Available</p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={handlePlayRecording}
                            className="p-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                          >
                            <Play className="w-5 h-5" />
                          </button>
                          <button
                            onClick={handleDownloadRecording}
                            className="p-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                          >
                            <Download className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    )}
                    {playbackError && (
                      <p className="mt-3 text-sm text-red-700">{playbackError}</p>
                    )}
                    {showPlayer && (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <p className="font-medium text-sm">Now Playing</p>
                          <button
                            onClick={() => setShowPlayer(false)}
                            className="text-gray-500 hover:text-gray-700"
                          >
                            <XCircle className="w-5 h-5" />
                          </button>
                        </div>
                        <AudioPlayer src={authenticatedUrl} autoPlay={pressedPlay} elementRef={audioEl} />
                        <div className="flex justify-end">
                          <button
                            onClick={handleDownloadRecording}
                            className="flex items-center gap-2 px-3 py-1.5 text-sm bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                          >
                            <Download className="w-4 h-4" />
                            Download
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Transcript from cpass's store (flag call-transcripts; hidden on 404). */}
              <CallTranscriptPanel
                callId={(call as any)?.call_id || call.unique_id}
                organizationId={organizationId}
                onSeek={showPlayer && authenticatedUrl ? (sec) => { seekAudio(audioEl.current, sec); } : undefined}
              />

              {/* Transfer Information */}
              {call.transferred_to && (
                <div>
                  <h4 className="font-semibold text-lg mb-3 flex items-center gap-2">
                    <ArrowRightLeft className="w-5 h-5" />
                    Transfer Information
                  </h4>
                  <div className="bg-orange-50 rounded-lg p-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-600">Transferred To</p>
                        <p className="font-medium">{call.transferred_to}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CallDetailsModal;